import { uuidModule } from "@/modules/uuid";
import { NoteModelData, NoteUpsertedSyncUpdateValue } from "@/store/note/types";
import {
  SpaceAccountNoteModelData,
  SpaceAccountNoteUpsertedSyncUpdateValue,
} from "@/store/recent-items/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { IAcceptNoteSuggestionOperation } from "@/store/sync/operations/types";
import { SyncModelData, OptimisticSyncUpdate, SyncOperationKind } from "@/store/sync/types";

export class AcceptNoteSuggestionOperation extends BaseSyncOperation<IAcceptNoteSuggestionOperation> {
  get operationKind(): SyncOperationKind {
    return "ACCEPT_NOTE_SUGGESTION";
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const note = await this.store.notes.getAsync(this.payload.note_id);
    if (!note?.suggestionData) return [];

    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    const spaceAccountNoteId = uuidModule.resolveSpaceAccountNoteSyncModelUuid({
      spaceAccountId,
      noteId: this.payload.note_id,
    });
    const spaceAccountNote = await this.store.spaceAccountNotes.getAsync(spaceAccountNoteId);
    if (!spaceAccountNote) return [];

    const noteValue: NoteUpsertedSyncUpdateValue = {
      model_id: this.payload.note_id,
      model_kind: "NOTE",
      model_version: note.modelVersion,
      model_data: {
        ...note.modelData,
        primary_label: note.suggestionData.suggested_primary_label,
        secondary_label: note.suggestionData.suggested_secondary_label,
      },
      model_scopes: note.modelScopes,
    };
    const noteUpserted: OptimisticSyncUpdate<NoteModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: noteValue,
    };

    const spaceAccountNoteUpsertedValue: SpaceAccountNoteUpsertedSyncUpdateValue = {
      model_id: spaceAccountNoteId,
      model_kind: "SPACE_ACCOUNT_NOTE",
      model_version: spaceAccountNote?.modelVersion ?? 0,
      model_data: {
        ...spaceAccountNote.modelData,
        suggestion_data: null,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };
    const spaceAccountNoteUpserted: OptimisticSyncUpdate<SpaceAccountNoteModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: spaceAccountNoteUpsertedValue,
    };

    return [noteUpserted, spaceAccountNoteUpserted];
  }

  async triggerRecompute() {
    const spaceAccountId = this.store.spaceAccounts.myPersonalSpaceAccountId;
    const spaceAccountNoteId = uuidModule.resolveSpaceAccountNoteSyncModelUuid({
      spaceAccountId,
      noteId: this.payload.note_id,
    });
    await this.store.spaceAccountNotes.recompute(spaceAccountNoteId);
    await this.store.notes.recompute(this.payload.note_id);
  }
}
